/**
 * FE-010: Stage Manager — Progressive Reveal State Machine
 *
 * Reactive state machine that coordinates stage transitions for the
 * progressive reveal flow. Listens to the session manager's currentStage
 * signal and tracks timing, progress, and reveal opt-in state.
 *
 * Stages:
 *   1 (voice only, 0–30s)
 *   2 (filtered video, 30–90s)
 *   3 (clear video, mutual opt-in)
 */

import { createSignal, createEffect, onCleanup, batch } from "solid-js";
import type { SessionManager } from "./session-manager";
import {
  STAGE_2_THRESHOLD_MS,
  REVEAL_ELIGIBLE_THRESHOLD_MS,
} from "@duocast/shared";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Reveal opt-in flow state. */
export type RevealState = "none" | "requested" | "waiting" | "accepted";

/** Transition currently happening, or null. */
export interface ActiveTransition {
  from: 1 | 2;
  to: 2 | 3;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Duration of each stage segment in milliseconds. */
const STAGE_1_DURATION_MS = STAGE_2_THRESHOLD_MS; // 30 000
const STAGE_2_DURATION_MS = REVEAL_ELIGIBLE_THRESHOLD_MS - STAGE_2_THRESHOLD_MS; // 60 000

/** How often we tick the elapsed time. */
const TICK_INTERVAL_MS = 250;

/** How long a stage transition overlay is shown (ms). */
export const TRANSITION_DURATION_MS = 2000;

// ---------------------------------------------------------------------------
// Stage Manager
// ---------------------------------------------------------------------------

export function createStageManager(session: SessionManager) {
  // -- Reactive signals ------------------------------------------------------

  /** Current stage (mirrors session.currentStage but may lag during transition). */
  const [currentStage, setCurrentStage] = createSignal<1 | 2 | 3>(1);

  /** Progress within the current stage segment (0..1). */
  const [stageProgress, setStageProgress] = createSignal(0);

  /** Time elapsed in the current stage in milliseconds. */
  const [timeInStage, setTimeInStage] = createSignal(0);

  /** Total elapsed time since the session connected, in milliseconds. */
  const [totalElapsed, setTotalElapsed] = createSignal(0);

  /** Whether the user is eligible to request a reveal (at 90s). */
  const [isRevealEligible, setIsRevealEligible] = createSignal(false);

  /** Reveal opt-in state machine. */
  const [revealState, setRevealState] = createSignal<RevealState>("none");

  /** Active transition overlay, if any. */
  const [activeTransition, setActiveTransition] =
    createSignal<ActiveTransition | null>(null);

  /** Whether a transition overlay is currently visible. */
  const [isTransitioning, setIsTransitioning] = createSignal(false);

  // -- Internal state --------------------------------------------------------

  let stageStartTime = 0;
  let sessionStartTime = 0;
  let tickTimer: ReturnType<typeof setInterval> | null = null;
  let transitionTimer: ReturnType<typeof setTimeout> | null = null;
  let isRunning = false;

  // -- Timer management ------------------------------------------------------

  function startTimer(): void {
    if (tickTimer) return;
    sessionStartTime = Date.now();
    stageStartTime = Date.now();
    isRunning = true;

    tickTimer = setInterval(() => {
      if (!isRunning) return;

      const now = Date.now();
      const elapsed = now - stageStartTime;
      const total = now - sessionStartTime;

      batch(() => {
        setTimeInStage(elapsed);
        setTotalElapsed(total);
        updateProgress(elapsed);
      });
    }, TICK_INTERVAL_MS);
  }

  function stopTimer(): void {
    isRunning = false;
    if (tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
    if (transitionTimer) {
      clearTimeout(transitionTimer);
      transitionTimer = null;
    }
  }

  function resetStageTimer(): void {
    stageStartTime = Date.now();
    setTimeInStage(0);
    setStageProgress(0);
  }

  // -- Progress calculation --------------------------------------------------

  function updateProgress(elapsedInStage: number): void {
    const stage = currentStage();
    let progress = 0;

    if (stage === 1) {
      progress = Math.min(elapsedInStage / STAGE_1_DURATION_MS, 1);
    } else if (stage === 2) {
      progress = Math.min(elapsedInStage / STAGE_2_DURATION_MS, 1);
    } else if (stage === 3) {
      // Stage 3 progress only fills when reveal is mutually accepted
      progress = revealState() === "accepted" ? 1 : 0;
    }

    setStageProgress(progress);
  }

  // -- Transition overlay management -----------------------------------------

  function showTransition(from: 1 | 2, to: 2 | 3): void {
    batch(() => {
      setActiveTransition({ from, to });
      setIsTransitioning(true);
    });

    // Auto-hide after transition duration
    if (transitionTimer) clearTimeout(transitionTimer);
    transitionTimer = setTimeout(() => {
      batch(() => {
        setActiveTransition(null);
        setIsTransitioning(false);
      });
    }, TRANSITION_DURATION_MS);
  }

  // -- Stage change effect ---------------------------------------------------

  createEffect(() => {
    const sessionStage = session.currentStage();
    const currentSessionState = session.sessionState();

    // Only run when connected
    if (currentSessionState !== "connected" && currentSessionState !== "connecting" && currentSessionState !== "matched") {
      return;
    }

    const prevStage = currentStage();

    if (sessionStage !== prevStage) {
      // Stage has changed
      if (sessionStage === 2 && prevStage === 1) {
        showTransition(1, 2);
        batch(() => {
          setCurrentStage(2);
          setIsRevealEligible(false);
        });
        resetStageTimer();
      } else if (sessionStage === 3 && prevStage === 2) {
        showTransition(2, 3);
        batch(() => {
          setCurrentStage(3);
          setIsRevealEligible(true);
        });
        resetStageTimer();
      } else {
        // Direct jump (e.g., reconnection to stage 3)
        batch(() => {
          setCurrentStage(sessionStage as 1 | 2 | 3);
          setIsRevealEligible(sessionStage >= 3);
        });
        resetStageTimer();
      }
    }
  });

  // -- Session state effect (start/stop timer) -------------------------------

  createEffect(() => {
    const state = session.sessionState();

    if (state === "connected") {
      if (!isRunning) {
        startTimer();
      }
    } else if (state === "ended" || state === "idle") {
      stopTimer();
      // Reset everything
      batch(() => {
        setCurrentStage(1);
        setStageProgress(0);
        setTimeInStage(0);
        setTotalElapsed(0);
        setIsRevealEligible(false);
        setRevealState("none");
        setActiveTransition(null);
        setIsTransitioning(false);
      });
    } else if (state === "waiting") {
      // Re-entering queue after next: reset
      stopTimer();
      batch(() => {
        setCurrentStage(1);
        setStageProgress(0);
        setTimeInStage(0);
        setTotalElapsed(0);
        setIsRevealEligible(false);
        setRevealState("none");
        setActiveTransition(null);
        setIsTransitioning(false);
      });
    }
  });

  // -- Reveal eligibility based on elapsed time ------------------------------

  createEffect(() => {
    const total = totalElapsed();
    if (total >= REVEAL_ELIGIBLE_THRESHOLD_MS && currentStage() >= 2) {
      setIsRevealEligible(true);
    }
  });

  // -- Peer disconnect handling ----------------------------------------------

  createEffect(() => {
    const left = session.peerLeft();
    if (left) {
      stopTimer();
      // Preserve current stage state for potential UI display
      // but reset reveal state
      setRevealState("none");
    }
  });

  // -- Cleanup ---------------------------------------------------------------

  onCleanup(() => {
    stopTimer();
  });

  // -- Public API ------------------------------------------------------------

  /**
   * User requests a reveal. Sends the request via session manager
   * and updates the local reveal state. The server does NOT notify
   * the peer until BOTH have opted in (privacy guarantee).
   */
  function requestReveal(): void {
    if (revealState() !== "none" || !isRevealEligible()) return;

    setRevealState("requested");
    session.revealRequest();
  }

  /**
   * Called when the server sends REVEAL_WAITING — local user has
   * requested, waiting for peer.
   */
  function handleRevealWaiting(): void {
    if (revealState() === "requested") {
      setRevealState("waiting");
    }
  }

  /**
   * Called when the server sends REVEAL_ACCEPT — both users agreed.
   */
  function handleRevealAccepted(): void {
    setRevealState("accepted");
  }

  /**
   * Reset the stage manager (e.g., when starting a new session).
   */
  function reset(): void {
    stopTimer();
    batch(() => {
      setCurrentStage(1);
      setStageProgress(0);
      setTimeInStage(0);
      setTotalElapsed(0);
      setIsRevealEligible(false);
      setRevealState("none");
      setActiveTransition(null);
      setIsTransitioning(false);
    });
  }

  return {
    // Reactive state
    currentStage,
    stageProgress,
    timeInStage,
    totalElapsed,
    isRevealEligible,
    revealState,
    activeTransition,
    isTransitioning,

    // Actions
    requestReveal,
    handleRevealWaiting,
    handleRevealAccepted,
    reset,

    // Constants (useful for components)
    STAGE_1_DURATION_MS,
    STAGE_2_DURATION_MS,
    TRANSITION_DURATION_MS,
  };
}

export type StageManager = ReturnType<typeof createStageManager>;
